// src/data/onboardingLetters.js
//
// THE ONBOARDING LETTERS - RULING 208 (Bob, 2026-09-22: "Fully approved"), task list section 347.
//
// Six letters, one per act on the application path. Built the way the claim letters (ruling 130) and the
// discipline letters (ruling 180) are built, and for the same reason:
//
//   * ASSEMBLED, NEVER COMPOSED. A letter is a named field from the record, or fixed wording written once by
//     a person. No model writes a word of one (ruling 201). Nothing here calls anything.
//   * ONE HEADER, from one place. letterHeader() in letterGroup.js, parent International first (ruling 188).
//   * WHAT IS NOT ON FILE SAYS SO. A missing field renders as [NOT ON FILE] and the letter is NOT sendable -
//     the same guard presentmentLetterGroup() and disciplineLetter() use.
//
// WHO IS COPIED - ruling 207, which is why every letter carries a `copies` list rather than leaving it to
// whoever prints it: every application is copied to the General Chairperson, and anything less than approval
// is flagged to him. `flagsGc: true` on a letter IS that flag; the desk colours what carries it.
//
// The platform SENDS none of these - it holds no mailer (task list section 341). Each is produced, filed
// where the act is filed, and handed over or posted by the officer whose name is on it.
import { NOT_ON_FILE, officeHeader, resolveContactBlocks, PARENT_ORGANIZATION } from './letterGroup';
import { MEETING_LABELS } from './obligationRecord';
import { markDraft } from './signoff';

const F = Object.freeze;

function nonEmpty(v) { return v !== null && v !== undefined && String(v).trim() !== ''; }
function field(obj, key, label, missing) {
  const v = obj ? obj[key] : null;
  if (nonEmpty(v)) return String(v).trim();
  missing.push(label);
  return NOT_ON_FILE;
}

// An application is not a claim, but its two office blocks resolve from the same office spine. Adapt, never
// duplicate the resolution (directives section XI). There is no railroad block on these letters: an
// application is between a man and his union, and the carrier is not a party to it.
function asAddressable(application) {
  const a = application || {};
  return {
    id: a.id || null,
    committee: a.generalCommittee || null,
    generalCommittee: a.generalCommittee || null,
    scopeKey: a.scopeKey || null,
    railroad: a.railroad || null,
    filedAt: a.submittedAt || a.decidedAt || null,
  };
}

export function applicationHeaderBlocks(application, ctx) {
  return resolveContactBlocks(asAddressable(application), ctx || {});
}

// L1..L6, exactly the set ruling 208 approved. `flagsGc` marks the ones that are "anything less than
// approval" (ruling 207.3) - they flag themselves to the General Chairperson when they are produced.
export const LETTERS = F({
  notice_to_appear: F({
    id: 'L1', title: 'NOTICE TO APPEAR - OBLIGATION OF MEMBERSHIP', to: 'applicant',
    copies: F(['local_committee', 'general_committee']), flagsGc: false, ruling: 'Article 16, Section 3(a)',
  }),
  approval: F({
    id: 'L2', title: 'APPROVAL OF APPLICATION AND WELCOME', to: 'applicant',
    copies: F(['general_committee']), flagsGc: false, ruling: 'rulings 193, 207.1',
  }),
  refusal: F({
    id: 'L3', title: 'DECISION ON APPLICATION - NOT APPROVED', to: 'applicant',
    copies: F(['general_committee']), flagsGc: true, ruling: 'rulings 193, 207.3',
  }),
  referral: F({
    id: 'L4', title: 'YOUR APPLICATION HAS BEEN REFERRED', to: 'applicant',
    copies: F(['general_committee']), flagsGc: true, ruling: 'rulings 60.1, 207.3',
  }),
  correction: F({
    id: 'L5', title: 'A CORRECTION TO YOUR APPLICATION', to: 'applicant',
    copies: F(['general_committee']), flagsGc: true, ruling: 'ruling 194.5',
  }),
  obligation_filed: F({
    id: 'L6', title: 'YOUR OBLIGATION OF MEMBERSHIP IS ON RECORD', to: 'member',
    copies: F(['local_committee', 'general_committee']), flagsGc: false, ruling: 'rulings 208.1, 208.2',
  }),
});

export const LETTER_KINDS = F(Object.keys(LETTERS));

// The fields each letter needs BEYOND the application itself. A desk can ask for exactly these and no more.
export const LETTER_INPUTS = F({
  notice_to_appear: F(['meetingDate', 'meetingPlace']),
  approval: F([]),
  refusal: F(['reason']),
  referral: F(['referredTo', 'reason']),
  correction: F(['fieldCorrected', 'wasValue', 'nowValue', 'source']),
  obligation_filed: F(['obligationDate', 'meetingType', 'presidingOfficer']),
});

// FIXED WORDING. Written once, here, in plain words. Nothing below is generated, and nothing below states
// an opinion about the man or his application.
const BODY = F({
  notice_to_appear: (app, i, m) => [
    'Your application to this local union has been accepted.',
    '',
    'You are notified to appear at a meeting of the local union to take the obligation of membership.',
    '',
    'Meeting date: ' + field(i, 'meetingDate', 'the date of the meeting', m),
    'Where: ' + field(i, 'meetingPlace', 'where the meeting is held', m),
    '',
    'The obligation is administered by the president or presiding officer, and you sign it as a matter of',
    'record. Bring identification. If you cannot attend on that date, tell your Local Chairperson - the',
    'obligation is taken at a later meeting, and nothing about your membership application changes.',
  ].join('\n'),

  approval: (app, i, m) => [
    'Your application has been approved. You are a member of this organization, and your account on Labor',
    'On The Record is open.',
    '',
    'Your Local Chairperson represents you on claims and discipline in your territory. Your General',
    'Chairperson holds authority over your agreement.',
    '',
    'Two things follow, and they are separate from each other:',
    '  1. You will be notified to appear at a meeting of the local union to take the obligation of',
    '     membership. Your account does not wait on it.',
    '  2. If you named an office on your application, that office is confirmed separately, by the officer',
    '     above the seat. Naming it granted nothing.',
    '',
    'Keep your information current. The facts you state are yours, and an error in them follows your claims.',
  ].join('\n'),

  refusal: (app, i, m) => [
    'Your application has not been approved.',
    '',
    'The reason: ' + field(i, 'reason', 'the reason for the decision', m),
    '',
    'This decision was made by your Local Chairperson and a copy of it is with your General Chairperson.',
    'If you believe it rests on a mistake of fact, say so to either of them - a decision made on a wrong',
    'fact is corrected on the record, not defended.',
  ].join('\n'),

  referral: (app, i, m) => [
    'Your application has been referred, and you are told so rather than left waiting.',
    '',
    'Referred to: ' + field(i, 'referredTo', 'who it was referred to', m),
    'Why: ' + field(i, 'reason', 'the reason for the referral', m),
    '',
    'A referral is not a refusal. Your application stands, and the officer it has been referred to decides',
    'it. Your reference number does not change.',
  ].join('\n'),

  correction: (app, i, m) => [
    'A value on your application has been corrected against the record, before any decision was made.',
    '',
    'What was corrected: ' + field(i, 'fieldCorrected', 'which value was corrected', m),
    'It read: ' + field(i, 'wasValue', 'what it read before', m),
    'It now reads: ' + field(i, 'nowValue', 'what it reads now', m),
    'Checked against: ' + field(i, 'source', 'what it was checked against', m),
    '',
    'You are told because the same value follows your claims. Check it before you file, and tell your Local',
    'Chairperson if the corrected value is itself wrong.',
  ].join('\n'),

  obligation_filed: (app, i, m) => [
    'Your obligation of membership is on record.',
    '',
    'Date taken: ' + field(i, 'obligationDate', 'the date it was taken', m),
    'At: ' + (nonEmpty(i && i.meetingType)
      ? (MEETING_LABELS[String(i.meetingType)] || (m.push('a meeting this Constitution recognises'), NOT_ON_FILE))
      : (m.push('which meeting it was taken at'), NOT_ON_FILE)),
    'Administered by: ' + field(i, 'presidingOfficer', 'the presiding officer', m),
    '',
    "The signed copy is filed in your Local's own records, where your Local Chairperson, your General",
    'Chairperson and you can read it, and nobody else can.',
  ].join('\n'),
});

function identity(application, missing) {
  const a = application || {};
  return [
    'APPLICATION ' + field(a, 'id', 'the application reference', missing),
    'Applicant: ' + field(a, 'fullLegalName', "the applicant's name", missing),
    'Craft and class: ' + field(a, 'craftAndClass', 'craft and class', missing),
    'Railroad: ' + field(a, 'railroad', 'railroad', missing),
    'Work location: ' + field(a, 'workLocation', 'work location', missing),
    'Local: ' + (nonEmpty(a.localCommittee) ? 'Local ' + String(a.localCommittee).trim() : 'assigned by the General Chairperson'),
    'General Committee: ' + field(a, 'generalCommittee', 'the General Committee', missing),
  ].join('\n');
}

const FOOT = '\n\n---\n' + PARENT_ORGANIZATION
  + '\nThis letter was assembled by Labor On The Record from the record of this application. It carries no'
  + '\nopinion, and nothing in it was written by a model.';

// ONE letter for ONE act. Same return shape as disciplineLetter(): { ok, letter, missingFields,
// unresolvedBlocks, sendable }. `sendable` is false while ANY field or header block is unresolved, so a desk
// cannot hand a member a letter with [NOT ON FILE] in it.
export function onboardingLetter(kind, application, blocks, input, opts) {
  const spec = LETTERS[kind];
  if (!spec) {
    return { ok: false, letter: null, missingFields: [], unresolvedBlocks: [], sendable: false,
      reason: '"' + kind + '" is not an act on the application path that produces a letter.' };
  }
  const missing = [];
  const b = blocks || applicationHeaderBlocks(application, {});
  // officeHeader, not letterHeader: no RAILROAD block on a letter between a man and his union.
  // RULING 229: the committee's letterhead banner (opts.letterhead) prints above it when one is set.
  const hdr = officeHeader(b, opts && opts.letterhead);
  const ident = identity(application, missing);
  const body = BODY[kind](application, input || {}, missing);
  // No railroad block on these letters, so only the two office blocks can be unresolved.
  const unresolvedBlocks = ['generalCommittee', 'localCommittee'].filter((k) => b[k] && !b[k].resolved).map((k) => b[k].kind);
  const uniq = Array.from(new Set(missing));
  const to = spec.to === 'member' ? 'the member' : 'the applicant';
  // RULING 231: a produced letter is a DRAFT until the officer signs off on it.
  const text = markDraft(hdr + '\n\nTO: ' + to + '\n\n' + spec.title + '\n\n' + ident + '\n\n' + body + FOOT);
  return {
    ok: true,
    letter: { kind, id: spec.id, title: spec.title, to: spec.to, copies: spec.copies, flagsGc: spec.flagsGc, ruling: spec.ruling, text },
    missingFields: uniq,
    unresolvedBlocks,
    sendable: uniq.length === 0 && unresolvedBlocks.length === 0,
  };
}

// Ruling 207.1: the General Chairperson is copied on every one of them. Stated as a function so a screen
// cannot quietly drop him from a letter's copies.
export function copiedTo(kind) {
  const spec = LETTERS[kind];
  return spec ? spec.copies.slice() : [];
}
